<script lang="ts" setup>
import AlertMessage from "@/components/AlertMessage.vue";
import type { TripMonitorRoute, TripMonitorTruck } from "@/api/trips/dto/trip-monitor";
import { computed, ref } from "vue";
import { RouteRedispatchState } from "@/enums/trip";
import { RouteStatus } from "@/data/trip";
import type { TripRouteDto } from "@/api/trips/dto/trip.dto";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";

const api = new API();
const { showSnackbarError } = useSnackbarStore();

const props = defineProps<{
  tripMonitorRoute: TripMonitorRoute;
  tripId: number;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "setDialogData", route: TripRouteDto, truck: TripMonitorTruck, tripId: number): void;
}>();

defineModel<boolean>({ default: false });

const route = ref<TripRouteDto | null>(null);
const loadingRouteInfo = ref<boolean>(false);

const isPlanned = computed(() => {
  return props.tripMonitorRoute.status === RouteStatus.planned;
});

const isRedispatchNotConfirmed = computed(() => {
  return props.tripMonitorRoute.redispatch === RouteRedispatchState.notConfirmed;
});

const alertText = computed(() => {
  if (isRedispatchNotConfirmed.value) {
    return "Re-dispatch is not confirmed yet";
  }

  if (!props.tripMonitorRoute.dispatchConfirmed) {
    return "Dispatch is not confirmed yet. Some details about trip are not available";
  }

  if (props.tripMonitorRoute.redispatch === RouteRedispatchState.needed) {
    return "All actions are disabled due to not synchronized changes in the order";
  }

  if (props.tripMonitorRoute.status === RouteStatus.planned) {
    return "This route is planned and will automatically become active once truck completes previous routes";
  }

  return "";
});

const alertStyle = computed<{ bg: string; border: string; iconColor: string; color?: string }>(() => {
  if (
    !props.tripMonitorRoute.dispatchConfirmed ||
    props.tripMonitorRoute.redispatch === RouteRedispatchState.notConfirmed
  ) {
    return {
      bg: "amber-lighten-5",
      border: "warning",
      iconColor: "warning",
    };
  }

  return {
    bg: "indigo-lighten-5",
    border: "none",
    iconColor: "indigo",
    color: "text-indigo",
  };
});

async function getRouteInfo() {
  loadingRouteInfo.value = true;
  if (!props.tripMonitorRoute.id || !props.tripId) {
    return;
  }

  try {
    const response = await api.trips.getRouteByTripId(props.tripId, props.tripMonitorRoute.id);

    if (response.data) {
      route.value = response.data;
    }
  } catch (error) {
    showSnackbarError("Could not get route by trip id");
  } finally {
    loadingRouteInfo.value = false;
  }
}

async function openRedispatchDialog() {
  await getRouteInfo();

  if (route.value) {
    emits("setDialogData", route.value, props.tripMonitorRoute.truck, props.tripId);
    emits("update:modelValue", true);
  }
}
</script>

<template>
  <alert-message
    v-if="alertText"
    :bg-color="alertStyle.bg"
    :border-color="alertStyle.border"
    :icon-color="alertStyle.iconColor"
  >
    <div>
      <span :class="alertStyle.color">
        {{ alertText }}
      </span>

      <router-link
        v-if="isPlanned"
        :to="{ name: 'truck-view', hash: '#active-routes', params: { id: props.tripMonitorRoute.truck.id } }"
        target="_blank"
      >
        <v-btn class="pa-1 ml-3" color="primary" size="xsmall" variant="outlined">View planned routes</v-btn>
      </router-link>

      <v-btn
        v-if="isRedispatchNotConfirmed"
        :loading="loadingRouteInfo"
        class="ml-3"
        color="primary"
        density="comfortable"
        size="small"
        variant="flat"
        @click="openRedispatchDialog"
        >Confirm re-dispatch</v-btn
      >
    </div>
  </alert-message>
</template>
